import { useState, useRef, useEffect, useCallback, ReactNode } from 'react';

import { debounce } from '../01-core/Utilities';
import { Swipeable } from '../01-core/Swipeable';
import { Media, MediaProps } from '../02-atoms/Media';
import { Button, ButtonProps } from '../02-atoms/Button';
import { SliderNavigation } from '../02-atoms/SliderNavigation';
import { Divider, DividerProps } from '../03-molecules/Divider';
import { ButtonGroup } from '../03-molecules/ButtonGroup';
import { Breadcrumb, BreadcrumbProps } from '../03-molecules/Breadcrumb';
import {
  InPageNavigation,
  InPageNavigationProps,
} from '../03-molecules/InPageNavigation';
import { TeaserBubble, TeaserBubbleProps } from '../03-molecules/TeaserBubble';
import { Tags, TagsProps } from '../03-molecules/Tags';
import {
  StickyActions,
  StickyActionsProps,
} from '../04-organisms/StickyActions';

export interface IntroSlideProps {
  /** The media of the slide */
  media: MediaProps;
  /** The optional title */
  title?: string;
  /** The optional description */
  description?: string;
  /** The optional actions */
  actions?: ButtonProps[];
}

export interface IntroProps {
  /** Type of intro */
  type?: 'regular' | 'leri' | 'full';
  /** Title */
  title?: string | ReactNode;
  /** Optional SEO title addition */
  seoTitleAddition?: string;
  /** Optional lead */
  lead?: string | ReactNode;
  /** Optional slides */
  slides?: IntroSlideProps[];
  /** Optional label for previous page */
  prevLabel?: string;
  /** Optional label for next page */
  nextLabel?: string;
  /** Optional label for swipe indicator */
  swipeLabel?: string;
  /** Optional divider */
  divider?: DividerProps;
  /** Optional actions */
  actions?: ButtonProps[];
  /** Optional breadcrumb */
  breadcrumb?: BreadcrumbProps;
  /** Optional in page navigation */
  navigation?: InPageNavigationProps;
  /** Optional teaser bubble */
  teaser?: TeaserBubbleProps;
  /** Optional tags */
  tags?: TagsProps['items'];
  /** Optional skip content button title */
  skip?: string;
  /** Optional sticky actions */
  stickyActions?: StickyActionsProps;
  /** Additional class */
  className?: string;
}

/** Block rendering the intro */
export const Intro = ({
  type = 'regular',
  title,
  seoTitleAddition,
  lead,
  slides,
  prevLabel,
  nextLabel,
  swipeLabel,
  divider,
  actions,
  breadcrumb,
  navigation,
  teaser,
  tags,
  skip,
  stickyActions,
  className,
  ...props
}: IntroProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const tape = useRef<HTMLDivElement | null>(null);
  if (!Array.isArray(slides)) {
    slides = [];
  }
  const pages: (string | undefined)[] = slides.map((slide) => slide.title);
  const [page, setPage] = useState(0);

  const swipeTo = (direction: string) => {
    if (direction === 'next') {
      setPage(Math.min(pages.length - 1, page + 1));
    } else if (direction == 'prev') {
      setPage(Math.max(0, page - 1));
    }
  };

  const slideTo = useCallback(
    (index: number) => {
      if (!slides.length || !tape.current || !ref.current) return;
      if (index !== page) setPage(index);
      const slide =
        tape.current.querySelectorAll<HTMLDivElement>('.intro__slide')[index];
      if (!slide) return;
      const current = slide.getBoundingClientRect();
      const left = tape.current.getBoundingClientRect().left;
      const offset = current.left - left;
      tape.current.style.setProperty('--offset', `${-offset}px`);
    },
    [slides.length, tape, ref, page],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(
      () => {
        slideTo(page);
      },
      100,
      false,
    ),
    [debounce, page, slideTo],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener('resize', handleResize);
    window.requestAnimationFrame(() => {
      slideTo(page);
    });
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, page, slideTo]);

  const scrollToContent = () => {
    if (!ref.current || typeof window === 'undefined') {
      return;
    }
    const target = ref.current.getBoundingClientRect().bottom + window.scrollY;
    window.scrollTo({
      top: target,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (page > slides.length - 1) {
      setPage(Math.max(0, slides.length - 1));
    }
  }, [slides.length, page]);

  if (slides.length === 0 && type === 'full') {
    type = 'regular';
  }
  const renderTeaserInFullContent = type === 'full' && Boolean(teaser);

  return (
    <>
      <section
        ref={ref}
        className={`intro intro--${type}${className ? ` ${className}` : ''}`}
        {...props}
      >
        <div className="intro__content">
          {type !== 'full' && breadcrumb && (
            <Breadcrumb className="intro__breadcrumb" {...breadcrumb} />
          )}
          {renderTeaserInFullContent && teaser && (
            <TeaserBubble className="intro__teaser" {...teaser} />
          )}
          {title && (
            <h1 className="intro__title">
              {title}
              {seoTitleAddition && (
                <small aria-hidden="true" className="intro__title-addition">
                  {', '}
                  {seoTitleAddition}
                </small>
              )}
            </h1>
          )}
          {type !== 'full' && lead && <p className="intro__lead">{lead}</p>}
          {type !== 'full' && tags && tags?.length > 0 && (
            <Tags className="intro__tags" items={tags} />
          )}
          {actions && (
            <ButtonGroup className="intro__actions" items={actions} />
          )}
          {type === 'full' && divider && (
            <Divider className="intro__divider" {...divider} />
          )}
        </div>
        {type === 'full' && breadcrumb && (
          <Breadcrumb className="intro__breadcrumb" {...breadcrumb} />
        )}
        {type === 'full' && lead && <p className="intro__lead">{lead}</p>}
        {type === 'full' && tags && tags?.length > 0 && (
          <Tags className="intro__tags" items={tags} />
        )}
        {type !== 'full' && divider && (
          <Divider className="intro__divider" {...divider} />
        )}
        {type === 'full' && lead && <p className="intro__lead">{lead}</p>}
        {teaser && slides?.length === 0 && !renderTeaserInFullContent && (
          <TeaserBubble className="intro__teaser" {...teaser} />
        )}
        {slides?.length > 0 && (
          <Swipeable
            className={[
              'intro__slides',
              slides.length > 1
                ? 'intro__slides--multiple'
                : 'intro__slides--single',
            ].join(' ')}
            onSwipeLeft={() => swipeTo('next')}
            onSwipeRight={() => swipeTo('prev')}
          >
            {skip && (
              <Button
                className="intro__skip"
                type="primary"
                title={skip}
                icon="arrow_down_triangle"
                onClick={scrollToContent}
              />
            )}
            {teaser && !renderTeaserInFullContent && (
              <TeaserBubble className="intro__teaser" {...teaser} />
            )}
            <div ref={tape} className="intro__slides-tape">
              {slides.map((slide, i) => (
                <div key={i} className="intro__slide">
                  <Media
                    className="intro__slide-media"
                    aspect={type === 'leri' ? 'landscape' : 'wide'}
                    {...slide.media}
                  />
                  {(slide.title ||
                    slide.description ||
                    (slide.actions && slide.actions?.length > 0)) &&
                    type !== 'full' && (
                      <div className="intro__slide-content">
                        {slide.title && <h2>{slide.title}</h2>}
                        {slide.description && <p>{slide.description}</p>}
                        {slide.actions && slide?.actions?.length > 0 && (
                          <ButtonGroup
                            className="intro__slide-actions"
                            items={slide.actions}
                          />
                        )}
                      </div>
                    )}
                </div>
              ))}
            </div>
            {slides.length > 1 && (
              <SliderNavigation
                pages={pages}
                page={page}
                swipe={true}
                onChange={slideTo}
                prevLabel={prevLabel}
                nextLabel={nextLabel}
                swipeLabel={swipeLabel}
                className="intro__slides-navigation"
              />
            )}
          </Swipeable>
        )}
        {(navigation?.items?.length ?? 0) > 0 && (
          <InPageNavigation className="intro__navigation" {...navigation} />
        )}
      </section>
      {(stickyActions?.actions?.length ?? 0) > 0 && (
        <StickyActions className="intro__sticky-actions" {...stickyActions} />
      )}
    </>
  );
};
